import { TENANT } from '../../tenant'

import {
  CONTACT_FORM_SUBJECT_KEY,
  type ContactFormSubjectKey,
} from './directContactFormSubjectCatalog'

export type ContactFormFieldRequirement = 'hidden' | 'optional' | 'required'

export type ContactFormLoggedOutNotificationType =
  'accountDeletion' | 'addressChange' | 'advertising' | 'nameChange'

type ContactFormSelfServiceAvailabilityStrategy =
  'afterSales' | 'documents' | 'orderModification' | 'tracking'

type ContactFormSelfServiceRouteStrategy =
  | 'changeOrder'
  | 'documents'
  | 'orderCancellation'
  | 'orderMissingOrIncorrectItem'
  | 'reportOrder'
  | 'returnOrder'
  | 'tracking'

export type ContactFormMessageVisibility = 'always' | 'loggedInOnly' | 'never'
export type ContactFormImageUploadPolicy =
  ContactFormFieldRequirement | 'requiredAboveDamagedProductThreshold'
type ContactFormLoginRequirement = 'always' | 'never' | 'withoutPrefilledOrder'

export type ContactFormSubjectPolicy = {
  availableTenants?: readonly TENANT[]
  directPageImageUpload: ContactFormImageUploadPolicy
  directPageMessageVisibility: ContactFormMessageVisibility
  isOrderRelated: boolean
  isProductRelated: boolean
  loggedOutNotification?: ContactFormLoggedOutNotificationType
  loginRequirement: ContactFormLoginRequirement
  orderNumberRequirement: ContactFormFieldRequirement
  productCodeRequirement: ContactFormFieldRequirement
  selfServiceAvailabilityStrategy?: ContactFormSelfServiceAvailabilityStrategy
  selfServiceRouteStrategy?: ContactFormSelfServiceRouteStrategy
  sharedFormImageUpload: ContactFormImageUploadPolicy
  sharedFormMessageVisibility: ContactFormMessageVisibility
}

const DEFAULT_SUBJECT_POLICY: ContactFormSubjectPolicy = {
  directPageImageUpload: 'hidden',
  directPageMessageVisibility: 'always',
  isOrderRelated: false,
  isProductRelated: false,
  loginRequirement: 'never',
  orderNumberRequirement: 'hidden',
  productCodeRequirement: 'hidden',
  sharedFormImageUpload: 'hidden',
  sharedFormMessageVisibility: 'always',
}

const makeSubjectPolicy = (
  policy: Partial<ContactFormSubjectPolicy>,
): ContactFormSubjectPolicy => ({ ...DEFAULT_SUBJECT_POLICY, ...policy })

const makeOrderSubjectPolicy = (
  policy: Partial<ContactFormSubjectPolicy> = {},
): ContactFormSubjectPolicy =>
  makeSubjectPolicy({
    isOrderRelated: true,
    loginRequirement: 'withoutPrefilledOrder',
    orderNumberRequirement: 'required',
    ...policy,
  })

const PRODUCT_SUBJECT_POLICY = {
  isProductRelated: true,
} as const

export const CONTACT_FORM_SUBJECT_POLICIES: Record<
  ContactFormSubjectKey,
  ContactFormSubjectPolicy
> = {
  [CONTACT_FORM_SUBJECT_KEY.ADVERTISING]: makeSubjectPolicy({
    directPageMessageVisibility: 'loggedInOnly',
    loggedOutNotification: 'advertising',
  }),
  [CONTACT_FORM_SUBJECT_KEY.CANCEL_CONTRACT]: makeOrderSubjectPolicy({
    loginRequirement: 'never',
    sharedFormMessageVisibility: 'never',
  }),
  [CONTACT_FORM_SUBJECT_KEY.CHANGE_ADDRESS]: makeSubjectPolicy({
    directPageMessageVisibility: 'loggedInOnly',
    loggedOutNotification: 'addressChange',
  }),
  [CONTACT_FORM_SUBJECT_KEY.CHANGE_NAME]: makeSubjectPolicy({
    directPageMessageVisibility: 'loggedInOnly',
    loggedOutNotification: 'nameChange',
  }),
  [CONTACT_FORM_SUBJECT_KEY.CHANGE_ORDER]: makeOrderSubjectPolicy({
    selfServiceAvailabilityStrategy: 'orderModification',
    selfServiceRouteStrategy: 'changeOrder',
  }),
  [CONTACT_FORM_SUBJECT_KEY.COPAYMENT_EXEMPTION]: makeSubjectPolicy({
    availableTenants: [TENANT.COM],
    directPageImageUpload: 'required',
    directPageMessageVisibility: 'loggedInOnly',
    loginRequirement: 'always',
    sharedFormImageUpload: 'required',
    sharedFormMessageVisibility: 'loggedInOnly',
  }),
  [CONTACT_FORM_SUBJECT_KEY.DEFER_PAYMENT]: makeOrderSubjectPolicy(),
  [CONTACT_FORM_SUBJECT_KEY.DELETE_ACCOUNT]: makeSubjectPolicy({
    directPageMessageVisibility: 'loggedInOnly',
    loggedOutNotification: 'accountDeletion',
  }),
  [CONTACT_FORM_SUBJECT_KEY.DOCTOR_ORDER]: makeSubjectPolicy({
    availableTenants: [TENANT.COM],
    directPageImageUpload: 'optional',
    directPageMessageVisibility: 'loggedInOnly',
    loginRequirement: 'always',
    sharedFormImageUpload: 'optional',
    sharedFormMessageVisibility: 'loggedInOnly',
  }),
  [CONTACT_FORM_SUBJECT_KEY.INVOICE_STATUS]: makeOrderSubjectPolicy({
    selfServiceAvailabilityStrategy: 'documents',
    selfServiceRouteStrategy: 'documents',
  }),
  [CONTACT_FORM_SUBJECT_KEY.MISSING_INVOICE]: makeOrderSubjectPolicy({
    selfServiceAvailabilityStrategy: 'documents',
    selfServiceRouteStrategy: 'documents',
  }),
  [CONTACT_FORM_SUBJECT_KEY.ORDER_CANCELLATION]: makeOrderSubjectPolicy({
    ...PRODUCT_SUBJECT_POLICY,
    selfServiceAvailabilityStrategy: 'orderModification',
    selfServiceRouteStrategy: 'orderCancellation',
  }),
  [CONTACT_FORM_SUBJECT_KEY.ORDER_MISSING_OR_INCORRECT_ITEM]:
    makeOrderSubjectPolicy({
      ...PRODUCT_SUBJECT_POLICY,
      directPageImageUpload: 'optional',
      selfServiceAvailabilityStrategy: 'afterSales',
      selfServiceRouteStrategy: 'orderMissingOrIncorrectItem',
    }),
  [CONTACT_FORM_SUBJECT_KEY.OTHER_CONCERN]: makeSubjectPolicy({}),
  [CONTACT_FORM_SUBJECT_KEY.PAYMENT_REMINDER]: makeOrderSubjectPolicy(),
  [CONTACT_FORM_SUBJECT_KEY.PHARMA_ADVICE]: makeSubjectPolicy({
    orderNumberRequirement: 'optional',
    productCodeRequirement: 'optional',
  }),
  [CONTACT_FORM_SUBJECT_KEY.PRODUCT_INQUIRY]: makeSubjectPolicy({
    orderNumberRequirement: 'optional',
    productCodeRequirement: 'required',
  }),
  [CONTACT_FORM_SUBJECT_KEY.REPORT_ORDER]: makeOrderSubjectPolicy({
    ...PRODUCT_SUBJECT_POLICY,
    directPageImageUpload: 'requiredAboveDamagedProductThreshold',
    selfServiceAvailabilityStrategy: 'afterSales',
    selfServiceRouteStrategy: 'reportOrder',
  }),
  [CONTACT_FORM_SUBJECT_KEY.RETURN_ORDER]: makeOrderSubjectPolicy({
    ...PRODUCT_SUBJECT_POLICY,
    selfServiceAvailabilityStrategy: 'afterSales',
    selfServiceRouteStrategy: 'returnOrder',
  }),
  [CONTACT_FORM_SUBJECT_KEY.TRACK_DELIVERY_ORDER]: makeOrderSubjectPolicy({
    selfServiceAvailabilityStrategy: 'tracking',
    selfServiceRouteStrategy: 'tracking',
  }),
}

export const CONTACT_FORM_NEUTRAL_SUBJECT_POLICY = makeSubjectPolicy({})
